import {useEffect, useRef, type ReactNode} from 'react'
import {Link, Navigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Hint} from '@/components/ui/hint'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {KairosMark} from '@/components/kairos-mark'
import {HallazgoFotoUpload} from '@/components/hallazgo-foto-upload'
import {ControlFotoUpload} from '@/components/control-foto-upload'
import {useAuth} from '@/hooks/platform/use-auth'
import {
  useMemberAcciones,
  useMemberPendingEvidence,
  useMemberSoportes,
  type MemberAction,
  type SgsstActionStatus,
} from '@/hooks/empresa/mi_espacio/use-member'
import {OnboardingLecturaFallida} from '@/components/onboarding-lectura-fallida'
import {useMyOnboarding} from '@/hooks/empresa/identidad/use-onboarding'
import {useMySolicitudes} from '@/hooks/sgsst/evidencias/use-solicitudes'
import {track} from '@/lib/telemetry'
import {formatShortDate, localToday} from '@/lib/risk'

// Ambos retoman en mi/ficha, que aterriza solo en su paso. `completar_perfil` falta a propósito:
// `MemberHome` redirige antes de pintar (KAI-219).
const PENDIENTE_ROUTE: Record<string, string> = {
  completar_ficha: 'mi/ficha',
  completar_entrevista: 'mi/ficha',
}

function CardEyebrow({children}: {children: ReactNode}) {
  return (
    <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">{children}</p>
  )
}

export function HomeLinkCard({
  to,
  eyebrow,
  title,
  body,
  cta,
  testId,
}: {
  to: string
  eyebrow: string
  title: ReactNode
  body?: string
  cta: string
  testId: string
}) {
  return (
    <Link
      to={to}
      className="surface surface-raised flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 p-5"
      data-testid={testId}>
      <div className="min-w-0 basis-64 grow">
        <CardEyebrow>{eyebrow}</CardEyebrow>
        <h3 className="mt-0.5 font-heading text-[17px] text-foreground">{title}</h3>
        {body && <p className="mt-1 text-sm text-muted-foreground">{body}</p>}
      </div>
      <span className="shrink-0 text-[13.5px] font-semibold text-primary">{cta}</span>
    </Link>
  )
}

function ApprovalStatusCard({aprobado}: {aprobado: boolean}) {
  const {t} = useTranslation()
  return (
    <div
      className="surface flex flex-wrap items-start justify-between gap-x-4 gap-y-2 p-5"
      data-testid="home-approval-card">
      <div className="min-w-0 basis-64 grow">
        <CardEyebrow>{t('member.home.approvalEyebrow')}</CardEyebrow>
        <h3 className="mt-0.5 font-heading text-[17px]">
          {t(aprobado ? 'member.home.approvalDone' : 'member.home.approvalPending')}
        </h3>
        {!aprobado && (
          <div className="mt-1.5">
            <Hint testId="home-approval-hint">{t('member.home.approvalPendingHint')}</Hint>
          </div>
        )}
      </div>
      <Tag tone={aprobado ? 'green' : 'amber'}>
        {t(aprobado ? 'member.home.approvalTagDone' : 'member.home.approvalTagPending')}
      </Tag>
    </div>
  )
}

function OnboardingCard({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: onboarding} = useMyOnboarding(businessId)
  if (!onboarding?.pendiente) return null
  const path = PENDIENTE_ROUTE[onboarding.pendiente.cta] ?? 'mi/ficha'
  return (
    <HomeLinkCard
      to={`/businesses/${businessId}/${path}`}
      eyebrow={t('member.home.onboardingEyebrow')}
      title={onboarding.pendiente.mensaje}
      cta={t('member.home.onboardingLink')}
      testId="home-onboarding-card"
    />
  )
}

/** Se aparta si la tarjeta de pendiente ya ofrece `completar_perfil`: nunca dos tarjetas para el mismo acto. */
function PerfilSociodemograficoCard({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: onboarding} = useMyOnboarding(businessId)
  if (!onboarding || onboarding.perfil_sellado_vigente || onboarding.pendiente?.cta === 'completar_perfil') return null
  return (
    <HomeLinkCard
      to={`/businesses/${businessId}/mi/perfil-sociodemografico`}
      eyebrow={t('member.home.perfilSociodemograficoEyebrow')}
      title={t('member.home.perfilSociodemograficoTitle')}
      cta={t('member.home.perfilSociodemograficoLink')}
      testId="home-perfil-sociodemografico-card"
    />
  )
}

/** Lee `pendiente_reconfirmacion`: durante una reconfirmación la escalera sigue en entrevista completa. */
function ReconfirmacionCard({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: onboarding} = useMyOnboarding(businessId)
  if (!onboarding?.pendiente_reconfirmacion) return null
  return (
    <HomeLinkCard
      to={`/businesses/${businessId}/mi/ficha`}
      eyebrow={t('member.home.reconfirmacionEyebrow')}
      title={t('member.home.reconfirmacionTitle')}
      cta={t('member.home.reconfirmacionLink')}
      testId="home-reconfirmacion-card"
    />
  )
}

/** Cada upload invalida esta misma consulta, así que la fila se va sola al subir. */
function EvidenciaPendienteCard({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: pending} = useMemberPendingEvidence(businessId)
  if (!pending) return null
  const {hallazgos, controles} = pending
  if (hallazgos.length === 0 && controles.length === 0) return null
  return (
    <div className="surface space-y-4 p-5" data-testid="home-evidencia-pendiente-card">
      <div>
        <CardEyebrow>{t('member.home.fotosRiesgosEyebrow')}</CardEyebrow>
        <h3 className="mt-0.5 font-heading text-[17px]">{t('member.home.fotosRiesgosTitle')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('member.home.fotosRiesgosHint')}</p>
      </div>

      {hallazgos.length > 0 && (
        <div className="space-y-3" data-testid="home-evidencia-hallazgos-group">
          <p className="font-mono text-[9px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
            {t('member.home.evidenciaGroupHallazgos')}
          </p>
          <ul className="space-y-3">
            {hallazgos.map((hallazgo) => (
              <li key={hallazgo.id} className="surface-divider space-y-1.5 border-t pt-3 first:border-t-0 first:pt-0">
                <p className="text-sm text-foreground">{hallazgo.foto_hint}</p>
                <HallazgoFotoUpload businessId={businessId} hallazgo={hallazgo} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {controles.length > 0 && (
        <div className="space-y-3" data-testid="home-evidencia-controles-group">
          <p className="font-mono text-[9px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
            {t('member.home.evidenciaGroupControles')}
          </p>
          <ul className="space-y-3">
            {controles.map((control) => (
              <li key={control.id} className="surface-divider space-y-1.5 border-t pt-3 first:border-t-0 first:pt-0">
                <p className="text-sm text-foreground">{control.texto}</p>
                {control.evidencia_hint && <p className="text-xs text-muted-foreground">📷 {control.evidencia_hint}</p>}
                <ControlFotoUpload businessId={businessId} controlId={control.id} hint={control.evidencia_hint} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const STATUS_TONE: Record<SgsstActionStatus, 'neutral' | 'amber' | 'green'> = {
  pendiente: 'neutral',
  en_curso: 'amber',
  cumplida: 'green',
}

function isOverdue(action: MemberAction): boolean {
  // nosemgrep: browser-clock-business-date -- overdue determination off the browser clock; tracked debt, not fixed in this wave (FE-02)
  return action.status !== 'cumplida' && action.target_date < localToday()
}

function AccionesCard({
  businessId,
  actions,
  total,
  done,
}: {
  businessId: string
  actions: MemberAction[]
  total: number
  done: number
}) {
  const {t} = useTranslation()
  // Overdue work leads; the API already returns them earliest-deadline first.
  const ordered = [...actions].sort((a, b) => Number(isOverdue(b)) - Number(isOverdue(a)))
  return (
    <div className="surface p-5" data-testid="home-acciones-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <CardEyebrow>{t('member.home.accionesEyebrow')}</CardEyebrow>
          <h3 className="mt-0.5 font-heading text-[17px]">{t('member.home.accionesTitle', {count: total})}</h3>
        </div>
        <span className="font-mono text-[11px] text-muted-foreground">
          {t('member.home.accionesDone', {done, total})}
        </span>
      </div>
      {ordered.map((action) => (
        <Link
          key={action.id}
          to={`/businesses/${businessId}/mi/acciones`}
          className="surface-divider -mx-5 flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5 border-t px-5 py-3 transition-colors first-of-type:mt-3 hover:bg-muted/45">
          <div className="min-w-0 basis-52 grow">
            <p className="text-sm text-foreground">{action.description}</p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              {action.standard_code && (
                <span className="font-mono text-xs font-semibold text-primary">{action.standard_code}</span>
              )}
              <span className="font-mono text-[12.5px] text-muted-foreground">
                {formatShortDate(action.target_date)}
              </span>
              {isOverdue(action) && <Tag tone="red">{t('plan.overdue')}</Tag>}
            </p>
          </div>
          <Tag tone={STATUS_TONE[action.status]}>{t(`plan.status.${action.status}`)}</Tag>
        </Link>
      ))}
      <Link
        to={`/businesses/${businessId}/mi/acciones`}
        className="mt-3 inline-block text-[13.5px] font-semibold text-primary hover:underline">
        {t('member.home.accionesLink')}
      </Link>
    </div>
  )
}

export function MemberHome({businessId, businessName}: {businessId: string; businessName: string}) {
  const {t} = useTranslation()
  const {user} = useAuth()
  const {data: onboarding, isError: onboardingError, refetch: refetchOnboarding} = useMyOnboarding(businessId)
  const {data: acciones, isLoading: accionesLoading} = useMemberAcciones(businessId)
  // Página mínima: de esta consulta solo se lee el `total` del servidor.
  const {data: accionesCumplidas} = useMemberAcciones(businessId, 1, 'cumplida', 1)
  const {data: soportes, isLoading: soportesLoading} = useMemberSoportes(businessId)
  const {data: solicitudes, isLoading: solicitudesLoading} = useMySolicitudes(businessId)

  const viewedRef = useRef<string | null>(null)
  useEffect(() => {
    if (viewedRef.current !== businessId) {
      viewedRef.current = businessId
      track('member_home_viewed', {business_id: businessId})
    }
  }, [businessId])
  const solItems = solicitudes?.items ?? []
  const solPending = solItems.filter((s) => s.state !== 'cumplida').length
  const solOverdue = solItems.filter((s) => s.state === 'vencida').length
  const firstName = user?.full_name?.split(' ')[0] ?? ''

  // Sin la lectura no se sabe si debe completar su perfil: no se le deja pasar como si no le faltara nada.
  if (onboardingError && !onboarding) return <OnboardingLecturaFallida onRetry={() => void refetchOnboarding()} />

  // Solo el espacio del trabajador redirige: una consola de administración no monta esta pantalla (KAI-219).
  if (onboarding?.pendiente?.cta === 'completar_perfil') {
    return <Navigate to={`/businesses/${businessId}/mi/ficha`} replace />
  }

  return (
    <div className="space-y-4" data-testid="member-home">
      <div>
        <div className="flex items-center gap-2 text-foreground">
          <KairosMark />
          <h1 className="font-heading text-2xl tracking-tight">{t('member.home.greeting', {name: firstName})}</h1>
        </div>
        <p className="mt-1.5 text-sm text-muted-foreground">{businessName}</p>
      </div>

      {onboarding && <ApprovalStatusCard aprobado={onboarding.aprobado} />}

      <HomeLinkCard
        to={`/businesses/${businessId}/incidentes/reportar`}
        eyebrow={t('member.home.incidenteEyebrow')}
        title={t('member.home.incidenteTitle')}
        cta={t('member.home.incidenteLink')}
        testId="home-reportar-incidente-card"
      />

      <OnboardingCard businessId={businessId} />

      <ReconfirmacionCard businessId={businessId} />

      <EvidenciaPendienteCard businessId={businessId} />

      <PerfilSociodemograficoCard businessId={businessId} />

      <HomeLinkCard
        to={`/businesses/${businessId}/mi/solicitudes`}
        eyebrow={t('member.home.solicitudesEyebrow')}
        cta={t('member.home.solicitudesLink')}
        testId="home-solicitudes-card"
        title={
          solicitudesLoading ? (
            t('member.home.solicitudesTitle')
          ) : solPending === 0 ? (
            t('solicitudes.mine.emptyTitle')
          ) : (
            <>
              {t('member.home.solicitudesPending', {count: solPending})}
              {solOverdue > 0 && (
                <span className="text-destructive"> · {t('solicitudes.overdueCount', {count: solOverdue})}</span>
              )}
            </>
          )
        }
      />

      {accionesLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : acciones && acciones.total > 0 ? (
        <AccionesCard
          businessId={businessId}
          actions={acciones.items}
          total={acciones.total}
          done={accionesCumplidas?.total ?? 0}
        />
      ) : (
        <div className="surface p-5" data-testid="home-acciones-empty">
          <CardEyebrow>{t('member.home.accionesEyebrow')}</CardEyebrow>
          <p className="mt-1 text-sm text-muted-foreground">{t('member.home.accionesEmpty')}</p>
        </div>
      )}

      {!soportesLoading && (
        <div
          className="surface flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-5"
          data-testid="home-soportes-line">
          <div className="min-w-0 basis-56 grow">
            <CardEyebrow>{t('member.soportes.title')}</CardEyebrow>
            <p className="mt-0.5 text-sm text-foreground">
              {t('member.home.soportesCount', {count: soportes?.total ?? 0})}
            </p>
          </div>
          <Link
            to={`/businesses/${businessId}/mi/soportes`}
            className="shrink-0 text-[13.5px] font-semibold text-primary hover:underline">
            {t('member.home.soportesLink')}
          </Link>
        </div>
      )}
    </div>
  )
}
